import { type ValueOf } from '@models/helpers/custom'
import { type CurrencyCodeType } from '@models/entities/currency'
import { type PaymentMethodType } from '@models/entities/payment'

export const TransactionStatuses = {
  NEW: 'new',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed'
} as const

export const TransactionTypes = {
  ALL: '',
  PAYMENT: 'payment',
  PAYOUT: 'payout'
} as const

export const TransactionFilters = {
  POINT: 'point',
  CURRENCY: 'currency',
  METHOD: 'method',
  STATUS: 'status',
  TYPE: 'type',
  UPDATED_DATE_FROM: 'updated_date_from',
  UPDATED_DATE_TO: 'updated_date_to',
  AMOUNT_FROM: 'amount_from',
  AMOUNT_TO: 'amount_to',
  SEARCH: 'search'
} as const

export const TransactionsDetailsQuery = {
  TRANSACTION_ID: 'transaction_id',
  POINT_ID: 'point_id'
} as const

export type TransactionStatusType = ValueOf<typeof TransactionStatuses>
export type TransactionTypeType = ValueOf<typeof TransactionTypes>
export type TransactionFilterType = ValueOf<typeof TransactionFilters>
export type TransactionStatusChipColorMapType = Record<TransactionStatusType, { bgColor: string; color: string; }>

export type TransactionAmountType = {
  currency: CurrencyCodeType;
  value: string;
}

export const TransactionEntryStatuses = {
  DEPOSIT: 'deposit',
  HOLD: 'hold',
  UNHOLD: 'unhold',
  PAYOUT: 'payout',
  CANCELLED_PAYOUT: 'cancelled_payout'
}

export type TransactionEntryStatusType = ValueOf<typeof TransactionEntryStatuses>

export type TransactionEntryType = {
  created_date: string;
  status: TransactionEntryStatusType;
  amount: string;
  fee: string;
  charged: string;
}

export type TransactionEntryStatusChipColorMapType = Record<TransactionEntryStatusType, { bgColor: string; color: string; }>

export type TransactionPayerFieldType = {
  source: 's2s' | 'hosted';
  value: string;
}

export type TransactionPayerInfoRowType = {
  label: string;
  field: TransactionPayerFieldType | undefined | null;
}

export type TransactionPayerInfoMapGroupType = {
  title: string;
  rows: TransactionPayerInfoRowType[];
}

export type TransactionPayerInfoMapType = {
  generalInfo: TransactionPayerInfoMapGroupType;
  financialInfo: TransactionPayerInfoMapGroupType;
  contacts: TransactionPayerInfoMapGroupType;
  address: TransactionPayerInfoMapGroupType;
}

export type TransactionPayerType = {
  first_name?: TransactionPayerFieldType;
  last_name?: TransactionPayerFieldType;
  surname?: TransactionPayerFieldType;
  phone?: TransactionPayerFieldType;
  user_id?: TransactionPayerFieldType;
  email?: TransactionPayerFieldType;
  document_type?: TransactionPayerFieldType;
  document?: TransactionPayerFieldType;
  address?: TransactionPayerFieldType;
  street?: TransactionPayerFieldType;
  country?: TransactionPayerFieldType;
  country_code?: TransactionPayerFieldType;
  city?: TransactionPayerFieldType;
  birth_date?: TransactionPayerFieldType;
  postal_code?: TransactionPayerFieldType;
  state?: TransactionPayerFieldType;
  cpf?: TransactionPayerFieldType;
  iban?: TransactionPayerFieldType;
  user_ip?: TransactionPayerFieldType;
}

export type TransactionType = {
  id: string;
  callback_sent: boolean | null;
  channel_code: string;
  external_id: string | null;
  created_date: string;
  updated_date?: string;
  amount: TransactionAmountType;
  fee: string;
  charged: string;
  point: string;
  status: TransactionStatusType;
  type: TransactionTypeType;
  method: PaymentMethodType;
  postings: TransactionEntryType[];
  payer: TransactionPayerType;
}

export type TransactionIdListType = Pick<TransactionType, 'id' | 'external_id'>
